import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatDisplayDate } from "@/lib/utils";
import {
  Search,
  Plus,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Users,
  Handshake,
  Calendar,
  Eye,
  TrendingUp,
  TrendingDown,
  BarChart3,
  FileText,
  Activity,
  DollarSign,
  Gift,
  Clock,
  Target,
  Star,
  UserPlus,
} from "lucide-react";
import DataTablePagination from "@/components/ui/data-table-pagination";
import {
  useReferralCampaigns,
  useDeleteReferralCampaign,
  useToggleReferralCampaignStatus,
  formatRewardDisplay,
  getRewardTypeDisplay,
} from "@/api-services/hooks/useReferralCampaigns";
import {
  ReferralCampaign,
  StatusOption,
  RewardTypeOption,
  REWARD_TYPES,
  STATUS_OPTIONS,
} from "@/api-services/hooks/referralCampaignTypes";
import DeleteReferralCampaignConfirmation from "@/components/referral-campaigns/DeleteReferralCampaignConfirmation";

const ReferralCampaignsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusOption>("all");
  const [referrerRewardFilter, setReferrerRewardFilter] =
    useState<RewardTypeOption>("all");
  const [referredRewardFilter, setReferredRewardFilter] =
    useState<RewardTypeOption>("all");

  // Pagination state (0-indexed for API)
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Delete functionality state
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [referralCampaignToDelete, setReferralCampaignToDelete] = useState<{
    id: number;
    details: string;
  } | null>(null);

  
  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms debounce delay

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Determine if we need client-side filtering
  const hasClientSideFilters = debouncedSearchTerm.trim() !== "" || statusFilter !== "all" || referrerRewardFilter !== "all" || referredRewardFilter !== "all";
  
  // When client-side filters are active, fetch a large page size to have enough data to filter
  // Otherwise, use normal pagination
  const apiPageSize = hasClientSideFilters ? 1000 : itemsPerPage;
  const apiPage = hasClientSideFilters ? 0 : currentPage; // Always fetch from page 0 when filtering client-side

  // Reset to first page when search filter changes
  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearchTerm, statusFilter, referrerRewardFilter, referredRewardFilter]);

  // Fetch referral campaigns from API (all filters are client-side now for better search)
  const { data, isLoading: loading, error, refetch } = useReferralCampaigns(
    {
      page: apiPage,
      size: apiPageSize,
    },
    {
      onError: (err) => {
        toast.error(`Failed to load referral campaigns: ${err.message}`);
      },
    }
  );

  // Delete referral campaign hook
  const { mutate: deleteReferralCampaign, isPending: isDeleting } =
    useDeleteReferralCampaign({
      onSuccess: async (data, referralCampaignId) => {
        toast.success("Referral campaign deleted successfully!");
        // Invalidate all referral campaigns queries to ensure fresh data
        await queryClient.invalidateQueries({ queryKey: ['referral-campaigns'] });
      },
      onError: (error, referralCampaignId) => {
        toast.error(`Failed to delete referral campaign: ${error.message}`);
      },
    });

  // Toggle referral campaign status hook
  const { mutate: toggleStatus, isPending: isToggling } =
    useToggleReferralCampaignStatus({
      onSuccess: async (data, referralCampaignId) => {
        toast.success("Referral campaign status updated successfully!");
        // Update only the changed row in the cache instead of refetching entire table
        queryClient.setQueriesData(
          { queryKey: ['referral-campaigns'] },
          (oldData: any) => {
            if (!oldData?.content) return oldData;

            const updatedContent = oldData.content.map((campaign: any) =>
              campaign.referralCampaignId === referralCampaignId
                ? { ...campaign, isActive: !campaign.isActive }
                : campaign
            );

            return {
              ...oldData,
              content: updatedContent,
            };
          }
        );
      },
      onError: (error, referralCampaignId) => {
        toast.error(
          `Failed to update referral campaign status: ${error.message}`
        );
      },
    });

  const getStatusBadge = useCallback((isActive: boolean) => {
    return isActive
      ? {
          variant: "default",
          className: "bg-success text-success-foreground",
          label: "Active",
        }
      : {
          variant: "secondary",
          className: "bg-muted text-muted-foreground",
          label: "Inactive",
        };
  }, []);

  const handleReferralCampaignAction = (
    action: string,
    referralCampaignId: number,
    referralCampaignDetails: string
  ) => {
    if (action === "Edit") {
      navigate(`/admin/referral-campaigns/edit/${referralCampaignId}`);
    } else if (action === "View") {
      navigate(`/admin/referral-campaigns/view/${referralCampaignId}`);
    } else if (action === "Toggle Status") {
      toggleStatus(referralCampaignId);
    }
  };

  // Delete handler functions
  const handleDeleteClick = (
    referralCampaignId: number,
    referralCampaignDetails: string
  ) => {
    setReferralCampaignToDelete({
      id: referralCampaignId,
      details: referralCampaignDetails,
    });
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirm = (referralCampaignId: number) => {
    deleteReferralCampaign(referralCampaignId);
    setDeleteConfirmationOpen(false);
    setReferralCampaignToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmationOpen(false);
    setReferralCampaignToDelete(null);
  };


  // Filter referral campaigns by search term, status and reward types on the client side
  const referralCampaigns = useMemo(() => {
    if (!data?.content) return [];

    let filtered = data.content;

    // Filter by search term (multiple fields)
    if (debouncedSearchTerm.trim() !== "") {
      const searchLower = debouncedSearchTerm.trim().toLowerCase();
      filtered = filtered.filter((campaign) => {
        // Search by campaign ID
        const idMatch = campaign.referralCampaignId?.toString().includes(searchLower);
        
        // Search by referrer reward type
        const referrerTypeMatch = campaign.referrerRewardType?.toLowerCase().includes(searchLower);
        
        // Search by referred reward type
        const referredTypeMatch = campaign.referredRewardType?.toLowerCase().includes(searchLower);
        
        // Search by referrer reward value
        const referrerValueMatch = campaign.referrerRewardValue?.toString().includes(searchLower);
        
        // Search by referred reward value
        const referredValueMatch = campaign.referredRewardValue?.toString().includes(searchLower);
        
        // Search by validity days
        const validityMatch = campaign.validityDays?.toString().includes(searchLower);
        
        // Search by max uses
        const maxUsesMatch = campaign.maxUsesPerReferrer?.toString().includes(searchLower);
        
        // Search by creator
        const creatorMatch = campaign.createdBy?.toLowerCase().includes(searchLower);
        
        // Search by formatted date
        const dateMatch = campaign.createdDate && formatDisplayDate(campaign.createdDate).toLowerCase().includes(searchLower);
        
        // Search by formatted reward displays
        const referrerRewardDisplay = formatRewardDisplay(
          campaign.referrerRewardType,
          campaign.referrerRewardValue
        ).toLowerCase();
        const referrerRewardMatch = referrerRewardDisplay.includes(searchLower);
        
        const referredRewardDisplay = formatRewardDisplay(
          campaign.referredRewardType,
          campaign.referredRewardValue
        ).toLowerCase();
        const referredRewardMatch = referredRewardDisplay.includes(searchLower);
        
        // Search by reward type display names
        const referrerTypeDisplayMatch = getRewardTypeDisplay(campaign.referrerRewardType)
          .toLowerCase()
          .includes(searchLower);
        const referredTypeDisplayMatch = getRewardTypeDisplay(campaign.referredRewardType)
          .toLowerCase()
          .includes(searchLower);
        
        return (
          idMatch ||
          referrerTypeMatch ||
          referredTypeMatch ||
          referrerValueMatch ||
          referredValueMatch ||
          validityMatch ||
          maxUsesMatch ||
          creatorMatch ||
          dateMatch ||
          referrerRewardMatch ||
          referredRewardMatch ||
          referrerTypeDisplayMatch ||
          referredTypeDisplayMatch
        );
      });
    }

    // Filter by status (active/inactive)
    if (statusFilter !== "all") {
      filtered = filtered.filter((campaign) => {
        if (statusFilter === "active") {
          return campaign.isActive === true;
        } else if (statusFilter === "inactive") {
          return campaign.isActive === false;
        }
        return true;
      });
    }

    // Filter by referrer reward type
    if (referrerRewardFilter !== "all") {
      filtered = filtered.filter((campaign) => {
        return campaign.referrerRewardType === referrerRewardFilter;
      });
    }

    // Filter by referred reward type
    if (referredRewardFilter !== "all") {
      filtered = filtered.filter((campaign) => {
        return campaign.referredRewardType === referredRewardFilter;
      });
    }

    return filtered;
  }, [data?.content, debouncedSearchTerm, statusFilter, referrerRewardFilter, referredRewardFilter, hasClientSideFilters]);

  // Pagination calculations - use filtered count for client-side filters
  const totalFilteredItems = referralCampaigns.length;
  const totalItems = data?.page?.totalElements || 0;
  
  // Calculate total pages based on filtered items
  const totalPages = useMemo(() => {
    if (hasClientSideFilters) {
      // Client-side filtering: paginate filtered results
      return Math.ceil(totalFilteredItems / itemsPerPage);
    }
    // Server-side pagination: use API response
    return data?.page?.totalPages || 0;
  }, [totalFilteredItems, itemsPerPage, hasClientSideFilters, data?.page?.totalPages]);

  // Paginated referral campaigns for current page (when using client-side filtering)
  const paginatedReferralCampaigns = useMemo(() => {
    if (!hasClientSideFilters) {
      // No client-side filtering, use API pagination
      return referralCampaigns;
    }
    // Client-side pagination for filtered results
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return referralCampaigns.slice(startIndex, endIndex);
  }, [referralCampaigns, currentPage, itemsPerPage, hasClientSideFilters]);

  const handlePageChange = (page: number) => {
    // Convert from 1-indexed (UI) to 0-indexed (API)
    setCurrentPage(page - 1);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(0); // Reset to first page when items per page changes
  };

  // Check if current page is empty after data loads and navigate to previous page if needed
  useEffect(() => {
    if (!loading && data && paginatedReferralCampaigns.length === 0 && currentPage > 0 && totalPages > 0) {
      // Current page is empty, navigate to the last valid page
      const lastPage = Math.max(0, totalPages - 1);
      setCurrentPage(lastPage);
    }
  }, [loading, paginatedReferralCampaigns.length, currentPage, totalPages, data]);

  // Format referral campaign details for display
  const getReferralCampaignDetails = useCallback(
    (referralCampaign: ReferralCampaign) => {
      return `Campaign ID: ${referralCampaign.referralCampaignId}`;
    },
    []
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">
            Loading referral campaigns data...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <TrendingDown className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              Error Loading Referral Campaigns
            </h3>
            <p className="text-muted-foreground mb-4">{error.message}</p>
            <Button onClick={() => refetch()}>Try Again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Referral Campaigns Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage and configure referral campaigns to encourage user growth and
            engagement
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            className="bg-gradient-primary hover:opacity-90"
            onClick={() => navigate("/admin/referral-campaigns/add")}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New Campaign
          </Button>
        </div>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by ID, reward type, value, creator, or date..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={(value: StatusOption) => setStatusFilter(value)}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={referrerRewardFilter}
              onValueChange={(value: RewardTypeOption) =>
                setReferrerRewardFilter(value)
              }
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Referrer Reward" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Referrer Rewards</SelectItem>
                {REWARD_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={referredRewardFilter}
              onValueChange={(value: RewardTypeOption) =>
                setReferredRewardFilter(value)
              }
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Referred Reward" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Referred Rewards</SelectItem>
                {REWARD_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Referral Campaigns Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Handshake className="w-5 h-5" />
            Referral Campaigns (
            {hasClientSideFilters 
              ? `${totalFilteredItems} filtered` 
              : totalItems}
            )
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campaign Details</TableHead>
                  <TableHead>Referrer Reward</TableHead>
                  <TableHead>Referred Reward</TableHead>
                  <TableHead>Validity Period</TableHead>
                  <TableHead>Usage Limits</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedReferralCampaigns.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center py-8 text-muted-foreground"
                    >
                      {referralCampaigns.length === 0 
                        ? "No referral campaigns found"
                        : "No referral campaigns match the current filters"}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedReferralCampaigns.map((referralCampaign) => {
                    const statusBadge = getStatusBadge(
                      referralCampaign.isActive
                    );
                    const referralCampaignDetails =
                      getReferralCampaignDetails(referralCampaign);

                    return (
                      <TableRow
                        key={referralCampaign.referralCampaignId}
                        className="hover:bg-muted/50"
                      >
                        <TableCell>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Target className="w-4 h-4 text-muted-foreground" />
                              <span className="font-semibold text-lg">
                                Campaign #{referralCampaign.referralCampaignId}
                              </span>
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {formatDisplayDate(referralCampaign.createdDate)}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <UserPlus className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm font-medium">
                                {getRewardTypeDisplay(
                                  referralCampaign.referrerRewardType
                                )}
                              </span>
                            </div>
                            <div className="font-bold text-green-600">
                              {formatRewardDisplay(
                                referralCampaign.referrerRewardType,
                                referralCampaign.referrerRewardValue
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm font-medium">
                                {getRewardTypeDisplay(
                                  referralCampaign.referredRewardType
                                )}
                              </span>
                            </div>
                            <div className="font-bold text-blue-600">
                              {formatRewardDisplay(
                                referralCampaign.referredRewardType,
                                referralCampaign.referredRewardValue
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="w-4 h-4 text-muted-foreground" />
                            <span>{referralCampaign.validityDays} days</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1 text-sm">
                            <div className="font-medium">
                              Max {referralCampaign.maxUsesPerReferrer} uses per
                              referrer
                            </div>
                            <div className="text-muted-foreground">
                              Per referrer limit
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusBadge.className}>
                            {referralCampaign.isActive ? (
                              <>
                                <ToggleRight className="w-3 h-3 mr-1" />
                                {statusBadge.label}
                              </>
                            ) : (
                              <>
                                <ToggleLeft className="w-3 h-3 mr-1" />
                                {statusBadge.label}
                              </>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onSelect={() => {
                                  navigate(
                                    `/admin/referral-campaigns/view/${
                                      referralCampaign.referralCampaignId || 0
                                    }`
                                  );
                                }}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() =>
                                  handleReferralCampaignAction(
                                    "Edit",
                                    referralCampaign.referralCampaignId || 0,
                                    referralCampaignDetails
                                  )
                                }
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Campaign
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() =>
                                  handleReferralCampaignAction(
                                    "Toggle Status",
                                    referralCampaign.referralCampaignId || 0,
                                    referralCampaignDetails
                                  )
                                }
                                disabled={isToggling}
                              >
                                {referralCampaign.isActive ? (
                                  <>
                                    <ToggleLeft className="w-4 h-4 mr-2" />
                                    Deactivate
                                  </>
                                ) : (
                                  <>
                                    <ToggleRight className="w-4 h-4 mr-2" />
                                    Activate
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() =>
                                  handleDeleteClick(
                                    referralCampaign.referralCampaignId || 0,
                                    referralCampaignDetails
                                  )
                                }
                                className="text-destructive"
                                disabled={isDeleting}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete Campaign
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            {/* Pagination - Always show if totalPages > 0, even if current page is empty */}
            {totalPages > 0 && (
              <DataTablePagination
                currentPage={currentPage + 1} // Convert from 0-indexed to 1-indexed for UI
                totalPages={totalPages}
                totalItems={hasClientSideFilters ? totalFilteredItems : totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                className="mt-4"
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delete Referral Campaign Confirmation Modal */}
      <DeleteReferralCampaignConfirmation
        open={deleteConfirmationOpen}
        onOpenChange={handleDeleteCancel}
        referralCampaignDetails={referralCampaignToDelete?.details || ""}
        referralCampaignId={referralCampaignToDelete?.id || 0}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default ReferralCampaignsPage;
